// React imports
import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import productService from "services/product-service";
import {AuthContext} from "context";

// E4A Components
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import Footer from "e4acomponents/Footer";

// Dashboard & Material UI components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";

import ProductDetailsFields from "../components/input-product-details";
import FileUploads from "../components/file-uploads";
import SelectPartCode from "../../Inventory-management/components/select-partcode";
import PartCodeDetails from "../../Inventory-management/components/partcode-details";
import LocationFields from "../../Inventory-management/components/item-fields/location-fields";
import SelectOrder from "../../fulfillment-center/components/select-order";
import {
    validateProductData,
    validateFilesData,
    validatePartCodeSelection
} from "../components/validators.js";

export default function AddProduct({ }) {
    const [filesData, setFilesData] = useState([]);
    const [product, setProduct] = useState({});
    const [partCode, setPartCode] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const [productErrors, setProductErrors] = useState({});
    const authContext = useContext(AuthContext);

    const navigate = useNavigate();

    const changeHandler = (name, value) => {
        setProduct({
            ...product,
            [name]: value,
        })
    }
    const batchChangeHandler = (updates) => {
        setProduct({
            ...product,
            ...updates,
        })
    }

    // Fields tied to the files to upload.
    const fields = [
        { label: 'File Name', name: 'fileName' },
        { label: 'Issue Date', name: 'issueDate', type: 'date' },
        { label: 'Expiry Date', name: 'expiryDate', type: 'date' },
    ]

    const errorProps = {
        'border': '1px solid red',
        'borderRadius': '4px',
        'padding': '8px',
    }

    const uploadFiles = async () => {
        let response = [];
        for (const fileData of filesData) {
            const payload = {
                file: fileData.file,
                data: {
                    attributes: {
                        fileName: fileData.fileName,
                        organization: product.organizationName,
                        folderPath: product.serialNumber,
                    }
                }
            }
            const uploadResp = await productService.uploadProductFile(payload);

            response.push({
                issueDate: fileData.issueDate,
                expiryDate: fileData.expiryDate,
                key: uploadResp.s3Key
            })
        }
        return response;
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        // Clear previous errors
        setProductErrors({});
        // Validate inputs
        const partCodeValid = validatePartCodeSelection(partCode);
        const productValid = validateProductData(product);
        const filesValid = validateFilesData(filesData);


        const errors = {
            ...(productValid || {}),
            ...(partCodeValid || {}),
            ...(filesValid || {})
        };

        if (Object.keys(errors).length > 0) {
            setProductErrors(errors);
            return;
        }

        // If all validations pass, proceed with form submission
        try {
            setIsLoading(true);
            let uploadedFiles = [];
            if (Object.keys(filesData).length > 0) {
                uploadedFiles = await uploadFiles();
            }

            const payload = {
                data: {
                    attributes: {
                        ...product,
                        partCode: partCode._id,
                        certifications: uploadedFiles || [],
                    },
                    logs: {
                        user: await authContext.getCurrentUser(),
                        comment: "",
                    }
                }
            }
            await productService.addProduct(payload);
            setIsLoading(false);
            navigate("/fulfillment-center");
        } catch (error) {
            console.error("Error adding product:", error);
        }
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox mt={5} mb={9}>
                <Grid container justifyContent="center" spacing={3}>

                    <Grid item xs={12}>

                        <MDBox mt={6} mb={8} textAlign="center">

                            {/* Title */}
                            <MDBox mb={1}>
                                <MDTypography variant="h3" fontWeight="bold">
                                    Add Product & Assign to Client
                                </MDTypography>
                            </MDBox>

                            {/* Subtitle */}
                            <MDBox>
                                <MDTypography variant="h5" fontWeight="regular" color="secondary">
                                    These informations will be used to describe the product.
                                </MDTypography>
                            </MDBox>

                        </MDBox>
                    </Grid>
                    
                    {isLoading ? (
                        <Grid item xs={12}>
                            <MDBox display="flex" justifyContent="center" alignItems="center" height="40vh">
                                <CircularProgress />
                            </MDBox>
                        </Grid>
                    ) : (<>
                        <Grid item xs={12} lg={5} justifyContent="space-around">
                            <MDBox sx={{ position: "sticky", top: (theme) => theme.spacing(12) }}>
                                {/* Select Part Code Component */}
                                <MDBox mb={3}>
                                    <SelectPartCode
                                        setSelectedPartCode={setPartCode}
                                        error={productErrors}
                                        changeHandler={changeHandler}
                                        editMode={false}
                                    />
                                </MDBox>
                                <MDBox>
                                    {/* Part Code Details Component */}
                                    {partCode && (
                                        <PartCodeDetails id={partCode._id} />
                                    )}
                                </MDBox>
                            </MDBox>
                        </Grid>

                        <Grid item xs={12} lg={7}>
                            {/* Card */}
                            <Card >
                                <MDBox
                                    component="form"
                                    onSubmit={onSubmit}
                                    p={3}
                                >

                                    {/* Product Details */}
                                    <MDBox m={2} mb={3}>
                                        <MDTypography variant="h4" fontWeight="bold">
                                            Product Details:
                                        </MDTypography>
                                        <ProductDetailsFields
                                            changeHandler={changeHandler}
                                            product={product}
                                            errors={productErrors}
                                        />
                                    </MDBox>

                                    {/* Location Assignment */}
                                    <MDBox m={2} mb={3}>
                                        <MDTypography variant="h4" fontWeight="bold" mt={1}>
                                            Assign Location :
                                        </MDTypography>
                                        <MDTypography variant="body2" color="text">
                                            Clients cannot view location details.
                                        </MDTypography>

                                        <MDBox mt={2} sx={productErrors.locationId ? errorProps : {}}>
                                            <LocationFields
                                                inventoryItem={product}
                                                changeHandler={changeHandler}
                                                batchChangeHandler={batchChangeHandler}
                                                commonProps={{ // Props common for the input fields
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    fullWidth: true,
                                                    marginBottom: "0.5rem",
                                                    marginTop: "0.5rem",
                                                }}
                                                editMode={false}
                                            />
                                        </MDBox>

                                        {productErrors.locationId && (
                                            <MDTypography variant="caption" color="error" ml={0.5}>
                                                {productErrors.locationId}
                                            </MDTypography>
                                        )}

                                    </MDBox>

                                    {/* Organization Assignment */}
                                    <MDBox m={2} mb={3}>
                                        <MDTypography variant="h4" fontWeight="bold" mt={1}>
                                            Assign To Order:
                                        </MDTypography>
                                        <MDTypography variant="body2" color="text">
                                            All users within the selected order's organization will be able to see this product.
                                        </MDTypography>

                                        <MDBox sx={productErrors.order ? errorProps : {}} mt={2}>
                                            <SelectOrder
                                                changeHandler={batchChangeHandler}
                                            />
                                        </MDBox>

                                        {productErrors.order && (
                                            <MDTypography variant="caption" color="error" ml={0.5}>
                                                {productErrors.order}
                                            </MDTypography>
                                        )}

                                    </MDBox>

                                    {/* File Uploads Section */}
                                    <MDBox m={2} mb={3}>
                                        <MDTypography variant="h4" fontWeight="bold" mt={1}>
                                            Certifications Upload:
                                        </MDTypography>
                                        <MDBox mt={2}>
                                            <FileUploads
                                                fields={fields}
                                                filesData={filesData}
                                                setFilesData={setFilesData}
                                                errors={productErrors}
                                            />
                                        </MDBox>
                                    </MDBox>

                                    {/* Submit & Cancel buttons */}
                                    <MDBox ml="auto" mt={4} mb={2} display="flex" justifyContent="flex-end">

                                        {/* Cancel Button */}
                                        <MDBox mx={1}>
                                            <MDButton
                                                variant="gradient" color="dark" size="small" px={2} mx={2}
                                                onClick={() => navigate(-1)}
                                            >
                                                Cancel
                                            </MDButton>
                                        </MDBox>

                                        {/* Submit Button */}
                                        <MDBox mx={1} >
                                            <MDButton variant="gradient" color="dark" size="small" type="submit">
                                                Add
                                            </MDButton>
                                        </MDBox>
                                    </MDBox>

                                </MDBox>
                            </Card>

                        </Grid>
                    </>)}
                </Grid>
                <MDBox mt={5}>
                    <Footer />
                </MDBox>
            </MDBox>

        </DashboardLayout>
    )
}